import { preloadImage } from '@mail/3promo';
import managePromoImgDark from 'img/safe/safePromo2Dark.png';
import managePromoImg from 'img/safe/safePromo2Light.png';
import safePromoCleanerCloudDarkMp4 from 'img/safe/safePromoCleanerCloud_dark.mp4';
import safePromoCleanerCloudDarkWebm from 'img/safe/safePromoCleanerCloud_dark.webm';
import safePromoCleanerCloudLightMp4 from 'img/safe/safePromoCleanerCloud_light.mp4';
import safePromoCleanerCloudLightWebm from 'img/safe/safePromoCleanerCloud_light.webm';
import safePromoCleanerMailDarkMp4 from 'img/safe/safePromoCleanerMail_dark.mp4';
import safePromoCleanerMailDarkWebm from 'img/safe/safePromoCleanerMail_dark.webm';
import safePromoCleanerMailLightMp4 from 'img/safe/safePromoCleanerMail_light.mp4';
import safePromoCleanerMailLightWebm from 'img/safe/safePromoCleanerMail_light.webm';
import defaultMovedFilesCoverDark from 'img/safe/safePromoDark.png';
import defaultMovedFilesCoverDarkMobile from 'img/safe/safePromoDarkMobile.png';
import defaultMovedLettersCoverDarkMobile from 'img/safe/safePromoLettersDarkMobile.png';
import defaultMovedFilesCoverLight from 'img/safe/safePromoLight.png';
import defaultMovedFilesCoverLightMobile from 'img/safe/safePromoLightMobile.png';
import { IS_PAID_USER } from 'reactApp/appHelpers/configHelpers';
import { quotaCleanerMobileVideoCovers } from 'reactApp/appHelpers/featuresHelpers/features/quotaCleaner';
import type { UserQuotaGroupId } from 'reactApp/types/QuotaCleaner';
import { sendDwh } from 'reactApp/utils/ga';
import { getPlatformInfo } from 'reactApp/utils/gaHelpers';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

const IMAGE_PRELOAD_TIMEOUT = 3 * 1000;

export const enum SafePromoType {
    base = 'base',
    afterMoveTrashbinFiles = 'afterMoveTrashbinFiles',
    afterMoveCleanerFiles = 'afterMoveCleanerFiles',
    afterMoveCleanerLetters = 'afterMoveCleanerLetters',
    moreAboutSafe = 'moreAboutSafe',
    manage = 'manage',
}

const AnalyticSafePromoTypeName = {
    [SafePromoType.base]: 'base',
    [SafePromoType.afterMoveTrashbinFiles]: 'trashbin',
    [SafePromoType.afterMoveCleanerFiles]: 'clnr-files',
    [SafePromoType.afterMoveCleanerLetters]: 'clnr-mail',
    [SafePromoType.moreAboutSafe]: 'about',
    [SafePromoType.manage]: 'manage',
} as const;

export type VideoCoverImg = { webm: string; mp4: string };

export const safePromoDialogCoverConfig: {
    dark: { [k in SafePromoType]: string | VideoCoverImg };
    light: { [k in SafePromoType]: string | VideoCoverImg };
} = {
    dark: {
        [SafePromoType.base]: defaultMovedFilesCoverDark,
        [SafePromoType.afterMoveTrashbinFiles]: defaultMovedFilesCoverDark,
        [SafePromoType.afterMoveCleanerFiles]: quotaCleanerMobileVideoCovers
            ? { webm: safePromoCleanerCloudDarkWebm, mp4: safePromoCleanerCloudDarkMp4 }
            : defaultMovedFilesCoverDarkMobile,
        [SafePromoType.afterMoveCleanerLetters]: quotaCleanerMobileVideoCovers
            ? { webm: safePromoCleanerMailDarkWebm, mp4: safePromoCleanerMailDarkMp4 }
            : defaultMovedLettersCoverDarkMobile,
        [SafePromoType.moreAboutSafe]: defaultMovedFilesCoverDark,
        [SafePromoType.manage]: managePromoImgDark,
    },
    light: {
        [SafePromoType.base]: defaultMovedFilesCoverLight,
        [SafePromoType.afterMoveTrashbinFiles]: defaultMovedFilesCoverLight,
        [SafePromoType.afterMoveCleanerFiles]: quotaCleanerMobileVideoCovers
            ? { webm: safePromoCleanerCloudLightWebm, mp4: safePromoCleanerCloudLightMp4 }
            : defaultMovedFilesCoverLightMobile,
        [SafePromoType.afterMoveCleanerLetters]: quotaCleanerMobileVideoCovers
            ? { webm: safePromoCleanerMailLightWebm, mp4: safePromoCleanerMailLightMp4 }
            : defaultMovedLettersCoverDarkMobile,
        [SafePromoType.moreAboutSafe]: defaultMovedFilesCoverLight,
        [SafePromoType.manage]: managePromoImg,
    },
};

export const isVideoCover = (promoType: SafePromoType) => {
    return typeof safePromoDialogCoverConfig.dark[promoType] === 'object';
};

export const sendSafeModalDwh = ({
    buttonName,
    action,
    promoType,
}: {
    action: 'show' | 'click' | 'close';
    buttonName?: 'ok' | 'more';
    groupId?: UserQuotaGroupId;
    promoType: SafePromoType;
}) => {
    const analyticPromoTypeName = AnalyticSafePromoTypeName[promoType] || 'none';
    const name = `safe-promo-dlg_${analyticPromoTypeName}`;

    const dwhData = {
        name,
        paid_user: IS_PAID_USER,
        platform: getPlatformInfo().join('_'),
        button_name: buttonName,
    };

    const optionalButtonField: (keyof typeof dwhData)[] = buttonName ? ['button_name'] : [];
    const dwh_enabled_fields: (keyof typeof dwhData)[][] = [['name', ...optionalButtonField, 'platform']];

    sendDwh({
        eventCategory: 'modal',
        action,
        dwh_enabled: true,
        dwh_enabled_fields,
        dwhData,
    });
};

export const preloadSafePromoDialogCover = async (promoType: SafePromoType) => {
    const isDarkTheme = isDarkThemeModeEnabled();
    const imageUrl =
        (isDarkTheme ? safePromoDialogCoverConfig.dark[promoType] : safePromoDialogCoverConfig.light[promoType]) ||
        safePromoDialogCoverConfig.light.base;

    if (isVideoCover(promoType)) {
        const { webm, mp4 } = imageUrl as VideoCoverImg;
        await preloadImage({ src: webm, timeout: IMAGE_PRELOAD_TIMEOUT });
        await preloadImage({ src: mp4, timeout: IMAGE_PRELOAD_TIMEOUT });
    } else {
        const src = imageUrl as string;
        await preloadImage({ src, timeout: IMAGE_PRELOAD_TIMEOUT });
    }
};
